import { FeatureFlags } from "@packages/feature-flags";
import { Product, ProductListConfig, ResultsInfo } from "./types/types";
import { useEnrichedProducts } from "./utils/useEnrichedProducts";
import { layoutMap as listLayouts } from "./product-list-layouts/layoutMap";
import { raise } from "./utils/raise";
import { FeatureFlagsProvider } from "./flags/flags";
import { useVisibleProducts } from "./utils/useVisibleProducts";
import { Ref, useEffect, useRef } from "react";
import { createProductListEventDispatcher } from "./utils/events/dispatchEvent";
import { PRODUCT_LIST_READY } from "./utils/events/productListEvents";
import { ProductListEventContext } from "./utils/events/useDispatchEvent";

type ProductListProps = {
  products: Product[];
  config: ProductListConfig;
  experiencesRawString?: string;
  resultsInfo: ResultsInfo;
  heading?: {
    main: string;
    subHeading: string | null;
  };
  flags: FeatureFlags;
  instanceId?: string;
};

export function ProductList({
  products,
  config,
  experiencesRawString,
  resultsInfo,
  heading,
  flags,
  instanceId,
}: ProductListProps) {
  const ref = useRef<HTMLElement | null>(null);
  const dispatch = createProductListEventDispatcher(ref);

  const visibleProducts = useVisibleProducts(products, flags);
  const enrichedProducts = useEnrichedProducts(visibleProducts, config);

  const { name: listLayoutName, props: listLayoutProps } =
    config["product-list-layout"];

  const ListLayout =
    listLayouts[listLayoutName] ||
    raise(`Couldn't find product-list layout "${listLayoutName}"`);

  useEffect(() => {
    if (ref.current) {
      dispatch({
        type: PRODUCT_LIST_READY,
        instanceId: instanceId || "",
      });
    }
  }, []);

  return (
    <FeatureFlagsProvider
      flags={flags}
      experiencesRawString={experiencesRawString}
      id="product-list"
    >
      <ProductListEventContext.Provider value={dispatch}>
        <template
          ref={ref as Ref<HTMLTemplateElement>}
          data-id="product-list-event-emitter"
        />
        <ListLayout
          config={config}
          products={enrichedProducts}
          configProps={listLayoutProps}
          resultsInfo={resultsInfo}
          heading={heading}
          instanceId={instanceId || ""}
        />
      </ProductListEventContext.Provider>
    </FeatureFlagsProvider>
  );
}
